import { useEffect, useState } from "react";
import { PhoneOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

interface VoicePaymentIssue {
  since: string;
  rejected: number;
}

const LOOKBACK_DAYS = 7;

async function readVoicePaymentIssue(orgId: string): Promise<VoicePaymentIssue | null> {
  const lookback = new Date(Date.now() - LOOKBACK_DAYS * 86_400_000).toISOString();
  const { data: lastRejected } = await supabase
    .from("voice_assistants_calls")
    .select("created_at")
    .eq("organization_id", orgId)
    .eq("disconnect_reason", "no_valid_payment")
    .gte("created_at", lookback)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!lastRejected) return null;

  const { data: lastAnswered } = await supabase
    .from("voice_assistants_calls")
    .select("created_at")
    .eq("organization_id", orgId)
    .gt("duration_ms", 0)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (lastAnswered && lastAnswered.created_at > lastRejected.created_at) return null;

  let firstQuery = supabase
    .from("voice_assistants_calls")
    .select("created_at")
    .eq("organization_id", orgId)
    .eq("disconnect_reason", "no_valid_payment")
    .order("created_at", { ascending: true })
    .limit(1);
  let countQuery = supabase
    .from("voice_assistants_calls")
    .select("id", { count: "exact", head: true })
    .eq("organization_id", orgId)
    .eq("disconnect_reason", "no_valid_payment");
  if (lastAnswered) {
    firstQuery = firstQuery.gt("created_at", lastAnswered.created_at);
    countQuery = countQuery.gt("created_at", lastAnswered.created_at);
  }
  const [{ data: first }, { count }] = await Promise.all([firstQuery.maybeSingle(), countQuery]);

  return {
    since: first?.created_at ?? lastRejected.created_at,
    rejected: count ?? 1,
  };
}

export function VoicePaymentBanner() {
  const { activeWorkspaceId } = useAuth();
  const [issue, setIssue] = useState<VoicePaymentIssue | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) {
      setIssue(null);
      return;
    }
    let mounted = true;
    const load = async () => {
      try {
        const next = await readVoicePaymentIssue(activeWorkspaceId);
        if (mounted) setIssue(next);
      } catch {
        if (mounted) setIssue(null);
      }
    };
    void load();
    const interval = setInterval(load, 5 * 60_000);
    return () => { mounted = false; clearInterval(interval); };
  }, [activeWorkspaceId]);

  if (dismissed || !issue) return null;

  const since = new Date(issue.since).toLocaleDateString("es-CO", { day: "numeric", month: "long" });
  const calls = `${issue.rejected.toLocaleString("es-CO")} ${issue.rejected === 1 ? "llamada" : "llamadas"}`;

  return (
    <div className="shadow-panel mx-3 mt-3 rounded-xl bg-destructive px-4 py-3 text-destructive-foreground">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <PhoneOff className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>Tu asistente de voz no está contestando.</strong>{" "}
            Desde el {since} se rechazaron {calls} por falta de saldo en la cuenta de voz. Contacta a un asesor.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDismissed(true)}
            className="text-destructive-foreground/80 hover:text-destructive-foreground text-xs px-2"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
